import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearAccountScopedStorage } from '@/lib/accountScopedStorage';
import { useEffectiveScopeId } from '@/hooks/useEffectiveScopeId';

export const AccountScopeBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const selectedAccountId = useEffectiveScopeId();
  const location = useLocation();
  const navigate = useNavigate();

  const [scopeId, setScopeId] = React.useState<string | null>(selectedAccountId);
  const isSwitching = selectedAccountId !== scopeId;

  const preSwitchLocationKeyRef = React.useRef(location.key);
  if (!isSwitching) {
    preSwitchLocationKeyRef.current = location.key;
  }

  React.useLayoutEffect(() => {
    if (!isSwitching) return;

    const navigatedWithSwitch = location.key !== preSwitchLocationKeyRef.current;

    if (scopeId !== null && !navigatedWithSwitch) {
      clearAccountScopedStorage();
      if (location.search || location.hash) {
        navigate(location.pathname, { replace: true });
        return;
      }
    }
    setScopeId(selectedAccountId);
  }, [isSwitching, scopeId, selectedAccountId, location, navigate]);

  if (isSwitching) return null;

  return <React.Fragment key={scopeId ?? 'no-account'}>{children}</React.Fragment>;
};

export default AccountScopeBoundary;
